export const OVERVIEW = ['recently_moved', 'planning_to_move', 'not_moved'] as const

export type HubOverview = (typeof OVERVIEW)[number]

export const LIVING_IN_DURATIONS = [
  '0_year',
  '1_2_years',
  '3_10_years',
  '11_years_or_more',
  'not_saying',
] as const
export type LivingInDurationOptions = (typeof LIVING_IN_DURATIONS)[number] | 'skipped'

export const PLANNING_TO_MOVE_DURATIONS = [
  'less_than_1_month',
  '2_6_months',
  '7_months_or_more',
  'dont_know',
] as const
export type PlanningToMoveDurationOptions = (typeof PLANNING_TO_MOVE_DURATIONS)[number] | 'skipped'

export const INTEREST_OPTIONS = [
  'supporting_relatives',
  'stay_in_touch',
  'travelling',
  'cross_border',
  'none_of_these',
] as const
export type interestOption = (typeof INTEREST_OPTIONS)[number] | 'skipped'

export type HubOnboardingStep =
  | 'welcome'
  | 'overview'
  | 'interests'
  | 'origin'
  | 'countries'
  | 'duration'
  | 'done'

export type UserSituation =
  | Exclude<interestOption, 'skipped' | 'none_of_these'>
  | 'living_in'
  | 'planning_to_move'
  | null

const USER_INFO = 'userInfo'

export type UserInfo = {
  onboardingStep: HubOnboardingStep
  overview?: HubOverview
  interestOptions?: interestOption[]
  userSituation?: UserSituation
  onboardingCompletedAt?: number
  countryLivingIn?: string
  countryOrigin?: string
  duration?: LivingInDurationOptions | PlanningToMoveDurationOptions
}

export async function getUserInfo() {
  return storage.getValue<UserInfo>(USER_INFO)
}

export async function setUserInfo(userInfo: UserInfo) {
  await storage.setValue(USER_INFO, userInfo)
}

export function getFlagEmoji(countryISO: string | undefined) {
  const base = 127397 // Unicode code point offset for regional indicator symbols
  const codePoints = (countryISO ?? '')
    .toUpperCase()
    .split('')
    .map((char) => char.charCodeAt(0) + base)
  if (codePoints.length !== 2) {
    return ''
  }
  return String.fromCodePoint(...codePoints)
}

export function useUserInfo() {
  const userInfo = useState<UserInfo>('userInfo', () => {
    const userInfo = ref<UserInfo>({
      onboardingStep: 'welcome',
    })

    getUserInfo()
      .then(async (value) => {
        // Retro-compatibility
        if (!value) {
          const userCountryInfo = await getUserCountryInfo()
          if (userCountryInfo) {
            if (
              userCountryInfo.countryLivingIn &&
              userCountryInfo.countryOrigin &&
              userCountryInfo.countryLivingIn !== userCountryInfo.countryOrigin &&
              (userCountryInfo.countryLivingInDuration === '0_year' ||
                userCountryInfo.countryLivingInDuration === '1_2_years')
            ) {
              userInfo.value = {
                onboardingStep: 'done',
                onboardingCompletedAt: userCountryInfo.onboardingCompletedAt,
                overview: 'recently_moved',
                userSituation: 'living_in',
                countryLivingIn: userCountryInfo.countryLivingIn,
                countryOrigin: userCountryInfo.countryOrigin,
                duration: userCountryInfo.countryLivingInDuration,
              }
            }
            await removeUserCountryInfo()
          }
          await setUserInfo(userInfo.value)
        } else {
          userInfo.value = value
        }

        watch(userInfo.value, async (value) => setUserInfo(value))
      })
      .catch(() => {})
    return userInfo
  })

  return userInfo
}

export async function clearUserInfo() {
  await storage.removeValue(USER_INFO)
}
